import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Scale, Info, List } from "lucide-react";
import "./Legal.css";

const CONTACT_EMAIL = "premiumprofitai@gmail.com";
const LAST_UPDATED = "April 2, 2026";

const SECTIONS = [
  {
    title: "Acceptance of Terms",
    content: (
      <>
        <p>
          These Terms of Service govern your access to and use of the Premium Profit platform,
          including our dashboards, analytics, charting tools, alerts, and connected account
          features.
        </p>
        <p>
          By creating an account, logging in, or otherwise using the Service, you agree to be bound
          by these Terms. If you do not agree, do not use Premium Profit.
        </p>
        <p>
          If you use the Service on behalf of a business or trading team, you represent that you
          have authority to bind that party to these Terms.
        </p>
      </>
    ),
  },
  {
    title: "Platform Scope",
    content: (
      <>
        <p>
          Premium Profit is a software platform built to help users monitor pricing gaps, review
          market data, and manage arbitrage-oriented workflows across supported brokers and trading
          accounts.
        </p>
        <ul>
          <li>Real-time premium tracking and broker-to-broker comparison tools.</li>
          <li>Trade monitoring, reporting, and account-level operational visibility.</li>
          <li>Interactive charts, historical records, and workflow controls.</li>
          <li>Administrative controls, user approvals, and account access management.</li>
        </ul>
        <div className="legal-callout">
          <Info size={16} />
          <span>
            Premium Profit is a technology product, not a broker, custodian, financial advisor, or
            investment manager. The platform does not execute discretionary trades on your behalf.
          </span>
        </div>
      </>
    ),
  },
  {
    title: "Eligibility and Account Access",
    content: (
      <>
        <p>
          To use Premium Profit, you must maintain an approved account and provide accurate
          registration information.
        </p>
        <ul>
          <li>
            You must be at least 18 years old and legally able to enter into a binding agreement.
          </li>
          <li>
            You are responsible for safeguarding your credentials, devices, API tokens, and any
            linked broker connections.
          </li>
          <li>
            You must promptly notify us if you suspect unauthorized access or a security incident
            affecting your account.
          </li>
          <li>
            You remain responsible for all activity that occurs under your account unless caused
            solely by our own security failure.
          </li>
        </ul>
        <p>
          Accounts may require manual approval before activation. We may reject, suspend, or revoke
          access where necessary to protect the platform, other users, or our legal obligations.
        </p>
      </>
    ),
  },
  {
    title: "Your Responsibilities",
    content: (
      <>
        <p>
          You are responsible for how you use the platform and for validating any trading workflow
          you configure through it.
        </p>
        <ul>
          <li>
            Use the Service only in compliance with applicable laws, regulations, broker rules, and
            contractual restrictions in your jurisdiction.
          </li>
          <li>
            Verify prices, spreads, balances, trade states, and execution assumptions before acting
            on platform outputs.
          </li>
          <li>
            Maintain valid permissions for every connected account, API, and data source you
            authorize through Premium Profit.
          </li>
          <li>
            Ensure your local environment, internet connection, and broker integrations are
            configured correctly.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "Referral Program Rules",
    content: (
      <>
        <p>
          Premium Profit may offer referral links, referral codes, commission tracking, or other
          incentive features through the platform.
        </p>
        <ul>
          <li>
            You may not self-refer, impersonate another person, or use misleading or spam-based
            promotion methods.
          </li>
          <li>
            Referral rewards, commissions, or credits may be delayed, adjusted, withheld, or
            reversed if we detect fraud, abuse, duplicate accounts, chargebacks, or policy
            violations.
          </li>
          <li>
            We may review referral activity manually and may modify or discontinue referral features
            at any time.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "Risk Disclosure",
    content: (
      <>
        <div className="legal-callout">
          <Info size={16} />
          <span>
            Forex, metals, and arbitrage strategies involve material risk. Market conditions,
            slippage, latency, rejected orders, broker behavior, and infrastructure issues can all
            lead to losses.
          </span>
        </div>
        <p>
          The data, analytics, alerts, and interface elements available through Premium Profit are
          provided for operational and informational purposes only. Nothing on the platform should
          be treated as financial, investment, tax, or legal advice.
        </p>
        <ul>
          <li>
            We do not guarantee that any signal, spread, premium value, or chart output is accurate,
            complete, or fit for your strategy.
          </li>
          <li>
            Past performance, historical pricing, or prior arbitrage behavior is not a guarantee of
            future opportunity or profit.
          </li>
          <li>
            You should trade only with capital you can afford to lose and only after independent
            review of the relevant risks.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "Acceptable Use Restrictions",
    content: (
      <>
        <p>
          You may not misuse the Service or attempt to interfere with its security, stability, or
          intended operation.
        </p>
        <ul>
          <li>
            Do not probe, scan, reverse engineer, scrape, or attempt to bypass access controls or
            rate limits.
          </li>
          <li>
            Do not use bots or automation against the platform except through features we expressly
            provide.
          </li>
          <li>
            Do not share accounts, resell access, mirror the interface, or redistribute proprietary
            reports without permission.
          </li>
          <li>
            Do not upload malicious code, attempt privilege escalation, or interfere with any other
            user&apos;s access.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "Third-Party Services and Data",
    content: (
      <>
        <p>
          Some parts of Premium Profit depend on third-party providers such as brokers, cloud
          infrastructure, authentication services, and market-data sources.
        </p>
        <ul>
          <li>
            We are not responsible for downtime, delays, pricing discrepancies, or API failures
            originating from third-party systems.
          </li>
          <li>
            Your relationship with a broker or connected provider remains governed by that
            provider&apos;s own terms and policies.
          </li>
          <li>
            You are responsible for securing any permissions, consents, and authorizations needed to
            connect outside services to Premium Profit.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "Service Availability",
    content: (
      <>
        <p>
          We aim to operate the Service reliably, but uninterrupted availability is not guaranteed.
          The platform may be limited, delayed, or unavailable for maintenance, security work,
          infrastructure issues, or external provider failures.
        </p>
        <ul>
          <li>
            We may modify, suspend, or retire features at any time, including where required by
            security, compliance, or provider constraints.
          </li>
          <li>
            We may apply usage limits, approval steps, or operational safeguards to protect the
            Service.
          </li>
        </ul>
        <p>
          Where practical, planned maintenance or material service-impacting changes will be
          communicated through the platform or by email.
        </p>
      </>
    ),
  },
  {
    title: "Intellectual Property",
    content: (
      <>
        <p>
          Premium Profit and its licensors retain all right, title, and interest in the Service,
          including the interface, visual design, software, data models, reports, documentation, and
          branding.
        </p>
        <ul>
          <li>
            These Terms grant you a limited, revocable, non-exclusive right to use the Service for
            its intended purpose.
          </li>
          <li>
            You may not copy, modify, distribute, sublicense, or create derivative works from the
            platform except as allowed by law or with our written consent.
          </li>
          <li>
            Our trademarks, logos, and product names may not be used without prior written
            permission.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "Disclaimers and Liability Limits",
    content: (
      <>
        <p>
          To the fullest extent permitted by law, the Service is provided on an &quot;as is&quot;
          and &quot;as available&quot; basis without warranties of any kind, whether express,
          implied, or statutory.
        </p>
        <ul>
          <li>
            We disclaim warranties of merchantability, fitness for a particular purpose,
            non-infringement, accuracy, availability, and uninterrupted operation.
          </li>
          <li>
            We are not liable for indirect, incidental, special, consequential, exemplary, or
            punitive damages, including lost profits, lost trading opportunity, or loss of data.
          </li>
          <li>
            We are not liable for losses caused by your trading decisions, broker conduct,
            third-party outages, credential compromise outside our control, or force majeure events.
          </li>
        </ul>
        <p>
          To the extent liability cannot be excluded, our aggregate liability for claims arising out
          of or relating to the Service will not exceed the amount you paid us for access to Premium
          Profit during the 12 months immediately preceding the event giving rise to the claim.
        </p>
      </>
    ),
  },
  {
    title: "Indemnity",
    content: (
      <>
        <p>
          You agree to defend, indemnify, and hold harmless Premium Profit, its operators, and
          affiliates from claims, liabilities, damages, and expenses arising out of:
        </p>
        <ul>
          <li>Your misuse of the Service or violation of these Terms.</li>
          <li>Your connected accounts, strategies, trades, or dealings with third parties.</li>
          <li>
            Your violation of applicable law, regulation, broker policy, or third-party rights.
          </li>
        </ul>
        <p>
          This includes reasonable legal fees and enforcement costs resulting from those claims.
        </p>
      </>
    ),
  },
  {
    title: "Suspension and Termination",
    content: (
      <>
        <p>
          We may suspend or terminate your access immediately if we believe it is necessary to
          protect the platform, comply with law, investigate abuse, respond to a security issue, or
          enforce these Terms.
        </p>
        <ul>
          <li>Suspension may be temporary while we investigate or remediate an issue.</li>
          <li>Termination does not limit any other rights or remedies available to us.</li>
          <li>
            Sections that should reasonably survive termination will remain in effect, including
            disclaimers, liability limits, indemnity, and intellectual property provisions.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: "Governing Law",
    content: (
      <p>
        These Terms are governed by the laws of India, without regard to conflict-of-law principles.
        Subject to applicable consumer protections, courts located in India will have exclusive
        jurisdiction over disputes arising out of or relating to these Terms or the Service.
      </p>
    ),
  },
  {
    title: "Changes to These Terms",
    content: (
      <>
        <p>
          We may update these Terms from time to time to reflect changes to the Service, our
          business practices, or legal requirements.
        </p>
        <p>
          When changes are material, we will provide notice through the platform, by email, or
          through another reasonable channel. Your continued use of Premium Profit after the revised
          Terms become effective means you accept the updated Terms.
        </p>
      </>
    ),
  },
  {
    title: "Contact Us",
    content: (
      <>
        <p>If you have any questions about these Terms of Service, please contact us:</p>
        <ul>
          <li>
            <strong>Email:</strong> <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </li>
          <li>
            <strong>Platform:</strong> Use the in-app support channel accessible from your
            dashboard.
          </li>
        </ul>
      </>
    ),
  },
];

export default function TermsOfService() {
  return (
    <div className="legal-page">
      <div className="legal-hero">
        <div className="legal-page-header">
          <div className="legal-page-title-row">
            <h1>
              <Scale />
              Terms of Service
            </h1>
            <span className="legal-page-header-badge">{LAST_UPDATED}</span>
          </div>
          <p className="legal-page-header-copy">
            The operating rules, platform boundaries, and risk disclosures that apply when you use
            Premium Profit.
          </p>
        </div>

        <Link to="/login" className="legal-back-link">
          <ArrowLeft size={14} />
          Back
        </Link>
      </div>

      <div className="legal-card legal-toc">
        <div className="legal-toc-header">
          <div className="legal-toc-header-copy">
            <div className="legal-toc-label">
              <List />
              <span>Table of Contents</span>
            </div>
            <p>Jump directly to the section you need without scanning the full page.</p>
          </div>
          <span className="legal-toc-count">{SECTIONS.length} sections</span>
        </div>
        <div className="legal-toc-grid">
          {SECTIONS.map((s, i) => (
            <a key={i} href={`#terms-${i + 1}`} className="legal-toc-item">
              <span className="legal-toc-num">{i + 1}</span>
              <span className="legal-toc-item-copy">
                <span className="legal-toc-item-title">{s.title}</span>
                <span className="legal-toc-item-meta">Open section</span>
              </span>
              <ArrowUpRight className="legal-toc-item-icon" size={15} />
            </a>
          ))}
        </div>
      </div>

      <div className="legal-card">
        <div className="legal-sections">
          {SECTIONS.map((s, i) => (
            <section key={i} className="legal-section" id={`terms-${i + 1}`}>
              <div className="legal-section-heading">
                <span className="legal-section-num">{i + 1}</span>
                <h2>{s.title}</h2>
              </div>
              {s.content}
            </section>
          ))}
        </div>
      </div>

      <footer className="legal-footer">
        <p>Premium Profit</p>
        <span>Terms of Service</span>
      </footer>
    </div>
  );
}
